import { Grade } from "domain/grade";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { ASSET_MANAGEMENT_PATH } from "services/asset-management/AssetService";
import { ParameterQuery } from "utils/commonFunctions";

export type PricingRuleAction = "CREATE" | "EDIT" | "DELETE";

export interface PricingRuleValidationError {
    index: number;
    field: string;
    message: string;
}

export interface PricingRuleErrorResponse {
    errors: PricingRuleValidationError[];
}

export interface PricingRuleValidationResult {
    valid: boolean;
    errors: PricingRuleValidationError[];
}

export const validatePricingRules = (rules: PricingRule[]): PricingRuleValidationResult => {
    const errors: PricingRuleValidationError[] = [];
    rules.forEach((rule, index) => {
        if (!rule.manufacturer.trim()) {
            errors.push({ index, field: "manufacturer", message: "Manufacturer is required" });
        }
        if (!rule.model.trim()) {
            errors.push({ index, field: "model", message: "Model is required" });
        }
        if (rule.value <= 0) {
            errors.push({ index, field: "value", message: "Value must be a positive number" });
        }
    });
    return { valid: errors.length === 0, errors };
};

export interface PricingRule {
    uuid?: string;
    manufacturer: string;
    model: string;
    blanccoGrade?: Grade;
    value: number;
    upliftValues?: string[];
    currencyCode?: string;
    action: PricingRuleAction;
}

export interface PricingRuleDto {
    uuid: string;
    manufacturer: string;
    model: string;
    blancco_grade?: Grade;
    value: number;
    uplift_values?: string[];
    currency_code: string;
}

export interface PricingRuleRequestDto {
    uuid?: string;
    manufacturer: string;
    model: string;
    blancco_grade?: Grade;
    value: number;
    uplift_values?: string[];
    currency_code?: string;
    action: PricingRuleAction;
}

export interface PricingRuleData {
    uuid: string;
    manufacturer: string;
    model: string;
    blanccoGrade?: Grade;
    value: number;
    upliftValues?: string[];
    currencyCode: string;
}

export interface PricingRulesPage {
    rules: PricingRuleData[];
    currentPage: number;
    totalPages: number;
    totalRules: number;
}

interface PricingRulesResponseDto {
    rules: PricingRuleDto[];
    current_page: number;
    total_pages: number;
    total_rules: number;
}

const PRICING_RULES_PATH = ASSET_MANAGEMENT_PATH + "pricing-rules";

const toPricingRuleDto = (rule: PricingRule): PricingRuleRequestDto => ({
    uuid: rule.uuid,
    manufacturer: rule.manufacturer,
    model: rule.model,
    blancco_grade: rule.blanccoGrade,
    value: rule.value,
    uplift_values: rule.upliftValues,
    currency_code: rule.currencyCode,
    action: rule.action,
});

export interface PricingRuleKeysResult {
    models: string[];
    manufacturers: string[];
}

export class PricingRuleService {
    private static toPricingRuleData(dto: PricingRuleDto): PricingRuleData {
        return {
            uuid: dto.uuid,
            manufacturer: dto.manufacturer,
            model: dto.model,
            blanccoGrade: dto.blancco_grade,
            value: dto.value,
            upliftValues: dto.uplift_values,
            currencyCode: dto.currency_code,
        };
    }

    // The page number is one-based. The search matches manufacturers, models, grades and uplift headlines.
    public fetchRulesPage(
        page: number,
        size: number,
        search?: string,
        abortController?: AbortController
    ): Promise<PricingRulesPage> {
        const queryString = new ParameterQuery()
            .add("page", String(page))
            .add("size", String(size))
            .add("search", search)
            .createQueryString();
        return apiGatewayService
            .invokeApi<null, PricingRulesResponseDto>(PRICING_RULES_PATH + queryString, "GET", null, {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((response) => ({
                rules: response.rules.map(PricingRuleService.toPricingRuleData),
                currentPage: response.current_page,
                totalPages: response.total_pages,
                totalRules: response.total_rules,
            }));
    }

    public applyRules(rules: PricingRule[], abortController?: AbortController): Promise<void> {
        return apiGatewayService
            .invokeApi<{ rules: PricingRuleRequestDto[] }, void>(
                PRICING_RULES_PATH,
                "POST",
                { rules: rules.map(toPricingRuleDto) },
                {
                    abortSignal: abortController?.signal,
                    emptyResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .catch((error: Error) => {
                try {
                    const parsed: PricingRuleErrorResponse = JSON.parse(error.message);
                    if (parsed.errors?.length) {
                        throw parsed;
                    }
                } catch (e) {
                    if ((e as PricingRuleErrorResponse).errors) {
                        throw e;
                    }
                }
                throw error;
            });
    }

    public fetchExistingKeysForRules(abortController: AbortController): Promise<PricingRuleKeysResult> {
        return apiGatewayService
            .invokeApi(PRICING_RULES_PATH + "/keys", "GET", null, {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((result: PricingRuleKeysResult) => result);
    }
}

export const pricingRuleService = new PricingRuleService();
